import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePagination } from '../hooks/usePagination';
import Pagination from './PaginationNew';
import { ExpenseRequestService } from '../services/expenseRequestService';
import type { ExpenseRequest } from '../types/expenseRequest';

const ExpenseRequestsTable: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const { currentPage, itemsPerPage, setPage, setLimit } = usePagination({
    defaultPage: 1,
    defaultLimit: 50,
  });

  const [rows, setRows] = useState<ExpenseRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [downloading, setDownloading] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [type, setType] = useState<string>('');
  const [creditCard, setCreditCard] = useState<string>('');

  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  useEffect(() => {
    document.title = currentPage > 1 ? `Expense Requests - Page ${currentPage}` : 'Expense Requests';
  }, [currentPage]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  const filters = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      type: type.trim() || undefined,
      creditCard: creditCard.trim() || undefined,
    }),
    [debouncedSearch, email, phone, type, creditCard]
  );

  useEffect(() => {
    if (!isAdmin) return;

    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await ExpenseRequestService.listExpenseRequests({
          page: currentPage,
          limit: itemsPerPage,
          sort: '-createdAt',
          ...filters,
        });

        if (!isMounted) return;
        setRows(res.items || []);
        setTotalPages(res.pages || 1);
        setTotalItems(typeof res.total === 'number' ? res.total : (res.items || []).length);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load expense requests');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAdmin, currentPage, itemsPerPage, filters]);

  const formatDate = (iso: string | undefined | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  const formatDateValue = (value: unknown) => {
    if (!value) return '—';
    if (typeof value === 'string') return formatDate(value);
    if (typeof value === 'object' && value && '$date' in (value as any) && typeof (value as any).$date === 'string') {
      return formatDate((value as any).$date);
    }
    return '—';
  };

  const getRequestData = (r: ExpenseRequest): any => {
    const d: any = (r as any).data;
    return d && typeof d === 'object' ? d : null;
  };

  const getUser = (r: ExpenseRequest): any => (r as any).userId ?? (r as any).user;

  const getUserEmail = (r: ExpenseRequest): string => {
    const u: any = getUser(r);
    if (u && typeof u === 'object' && typeof u.email === 'string' && u.email.trim()) return u.email.trim();
    const d = getRequestData(r);
    if (d && typeof d.email === 'string' && d.email.trim()) return d.email.trim();
    return '—';
  };

  const getUserPhone = (r: ExpenseRequest): string => {
    const u: any = getUser(r);
    const fromUser = u && typeof u === 'object' ? (u.phone ?? u.mobile) : null;
    if (typeof fromUser === 'string' && fromUser.trim()) return fromUser.trim();
    const d = getRequestData(r);
    if (d && typeof d.phone === 'string' && d.phone.trim()) return d.phone.trim();
    if (d && typeof d.mobile === 'string' && d.mobile.trim()) return d.mobile.trim();
    return '—';
  };

  const getType = (r: ExpenseRequest): string => {
    const d = getRequestData(r);
    const fromNotesType = d?.notes?.type;
    if (typeof fromNotesType === 'string' && fromNotesType.trim()) return fromNotesType.trim();
    if (typeof d?.type === 'string' && d.type.trim()) return d.type.trim();
    if (typeof (r as any).type === 'string' && String((r as any).type).trim()) return String((r as any).type).trim();
    return '—';
  };

  const getCreditCard = (r: ExpenseRequest): string => {
    const d = getRequestData(r);
    if (typeof d?.creditCard === 'string' && d.creditCard.trim()) return d.creditCard.trim();
    if (typeof (r as any).creditCard === 'string' && String((r as any).creditCard).trim()) return String((r as any).creditCard).trim();
    return '—';
  };

  const getAmount = (r: ExpenseRequest): string => {
    const d = getRequestData(r);
    const v = d?.amount ?? (r as any).amount;
    if (typeof v === 'number' && Number.isFinite(v)) return v.toLocaleString('en-IN');
    if (typeof v === 'string' && v.trim()) return v.trim();
    return '—';
  };

  const toText = (value: unknown): string => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const escapeCsvValue = (value: unknown): string => {
    const safe = toText(value).replace(/\r?\n|\r/g, ' ');
    return `"${safe.replace(/"/g, '""')}"`;
  };

  const fetchAllExpenseRequests = async (): Promise<ExpenseRequest[]> => {
    const limit = 500;
    let page = 1;
    const all: ExpenseRequest[] = [];

    // Recompute using non-debounced search too (so user can hit download quickly)
    const searchTerm = (debouncedSearch || search).trim();

    while (true) {
      const res = await ExpenseRequestService.listExpenseRequests({
        page,
        limit,
        sort: '-createdAt',
        search: searchTerm ? searchTerm : undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        type: type.trim() || undefined,
        creditCard: creditCard.trim() || undefined,
      });

      const items = res.items || [];
      all.push(...items);

      const reachedEnd = page >= (res.pages || 1) || items.length < limit;
      if (reachedEnd) break;
      page += 1;
    }

    return all;
  };

  const handleDownloadCsv = async () => {
    if (downloading) return;

    setDownloadError(null);
    setDownloading(true);

    try {
      const all = await fetchAllExpenseRequests();
      if (!all.length) {
        setDownloadError('No expense requests available to download.');
        return;
      }

      const columns = [
        { key: 'id', label: 'ID' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'amount', label: 'Amount' },
        { key: 'type', label: 'Type' },
        { key: 'creditCard', label: 'Credit Card' },
        { key: 'createdAt', label: 'Created At' },
        { key: 'updatedAt', label: 'Updated At' },
        { key: 'rawData', label: 'Raw Data' },
      ] as const;

      const rowsForCsv = all.map((r) => {
        const data = getRequestData(r);
        return {
          id: r._id,
          email: getUserEmail(r) === '—' ? '' : getUserEmail(r),
          phone: getUserPhone(r) === '—' ? '' : getUserPhone(r),
          amount: getAmount(r) === '—' ? '' : getAmount(r),
          type: getType(r) === '—' ? '' : getType(r),
          creditCard: getCreditCard(r) === '—' ? '' : getCreditCard(r),
          createdAt: formatDateValue((r as any).createdAt),
          updatedAt: formatDateValue((r as any).updatedAt),
          rawData: data ? JSON.stringify(data) : '',
        };
      });

      const header = columns.map((c) => escapeCsvValue(c.label)).join(',');
      const body = rowsForCsv
        .map((row) => columns.map((c) => escapeCsvValue((row as any)[c.key])).join(','))
        .join('\n');
      const csv = `${header}\n${body}`;

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `expense-requests-${stamp}.csv`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Failed to download CSV');
    } finally {
      setDownloading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md p-4 text-sm">
        You do not have permission to view expense requests.
      </div>
    );
  }

  if (loading && !rows.length) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading expense requests...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expense Requests</h1>
          <p className="mt-1 text-sm text-gray-500">Admin list of submitted expense requests.</p>
        </div>

        <button
          onClick={handleDownloadCsv}
          disabled={downloading}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {downloading ? 'Downloading…' : 'Download CSV'}
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>}
      {downloadError && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{downloadError}</div>
      )}

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="text"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setPage(1);
              }}
              placeholder="email"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Phone</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setPage(1);
              }}
              placeholder="phone"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Type</label>
            <input
              type="text"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setPage(1);
              }}
              placeholder="talk_to_expert"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Credit Card</label>
            <input
              type="text"
              value={creditCard}
              onChange={(e) => {
                setCreditCard(e.target.value);
                setPage(1);
              }}
              placeholder="Atlas"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credit Card</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {!rows.length ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                    No expense requests found.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDateValue((r as any).createdAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getUserEmail(r)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getUserPhone(r)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getAmount(r)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getType(r)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getCreditCard(r)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => navigate(`/expenses/requests/${encodeURIComponent(r._id)}`)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setPage}
        onLimitChange={(limit) => {
          setLimit(limit);
          setPage(1);
        }}
      />
    </div>
  );
};

export default ExpenseRequestsTable;
